import { SimManagementError } from '../models/Errors';
import {
  BundleAlertDefinition,
  BundleAlertDefinitionInputCreate,
  BundleAlertDefinitionInputUpdate,
} from '../models/transfer/input/BundleAlertDefinition';
import {
  CREATE_BUNDLE_ALERT_DEFINITION,
  CREATE_BUNDLE_ALERT_DEFINITION_FAILURE,
  CREATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
  DELETE_BUNDLE_ALERT_DEFINITION,
  DELETE_BUNDLE_ALERT_DEFINITION_FAILURE,
  DELETE_BUNDLE_ALERT_DEFINITION_SUCCESS,
  ENSURE_BUNDLE_ALERT_DEFINITIONS,
  LOAD_BUNDLE_ALERT_DEFINITIONS,
  LOAD_BUNDLE_ALERT_DEFINITIONS_FAILURE,
  LOAD_BUNDLE_ALERT_DEFINITIONS_PENDING,
  LOAD_BUNDLE_ALERT_DEFINITIONS_SUCCESS,
  UPDATE_BUNDLE_ALERT_DEFINITION,
  UPDATE_BUNDLE_ALERT_DEFINITION_FAILURE,
  UPDATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
} from './actionTypes';
import { Action, ActionMeta, PromiseAction, RejectFunc, ResolveFunc } from './commonActions';

/**
 * Dispatched to create a bundle alert definition.
 * @param payload An object containing the bundle alert definition to create.
 * @param resolve Optional function which will be called on success.
 * @param reject Optional function which will be called on failure.
 */
export function createBundleAlertDefinition(
  payload: BundleAlertDefinitionInputCreate,
  resolve?: ResolveFunc<BundleAlertDefinition>,
  reject?: RejectFunc
): PromiseAction<BundleAlertDefinitionInputCreate, BundleAlertDefinition> {
  const type = CREATE_BUNDLE_ALERT_DEFINITION;
  return { type, payload, resolve, reject };
}

/**
 * Action which is dispatched when a bundle alert definition failed to be created.
 * @param error The error which caused the action.
 */
export function createBundleAlertDefinitionFailure(error: SimManagementError): Action<SimManagementError> {
  return {
    error: true,
    type: CREATE_BUNDLE_ALERT_DEFINITION_FAILURE,
    payload: error,
  };
}

/**
 * Action which is dispatched when a bundle alert definition has been successfully created.
 * @param bundleAlertDefinition The bundle alert definition which was created.
 */
export function createBundleAlertDefinitionSuccess(
  bundleAlertDefinition: BundleAlertDefinition
): Action<BundleAlertDefinition> {
  return {
    type: CREATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
    payload: bundleAlertDefinition,
  };
}

/**
 * Action which is dispatched when a bundle alert definition is to be deleted.
 * @param definition The bundle alert definition.
 * @param resolve Optional function which will be called on success.
 * @param reject Optional function which will be called on failure.
 */
export function deleteBundleAlertDefinition(
  definition: BundleAlertDefinition,
  resolve?: ResolveFunc<BundleAlertDefinition>,
  reject?: RejectFunc
): PromiseAction<BundleAlertDefinition, BundleAlertDefinition> {
  return {
    type: DELETE_BUNDLE_ALERT_DEFINITION,
    payload: definition,
    resolve,
    reject,
  };
}

/**
 * Action which is dispached when a bundle alert definition is successfully deleted from the server.
 * @param bundleAlertDefinition The bundle alert definition that was deleted.
 */
export function deleteBundleAlertDefinitionSuccess(
  bundleAlertDefinition: BundleAlertDefinition
): Action<BundleAlertDefinition> {
  return {
    type: DELETE_BUNDLE_ALERT_DEFINITION_SUCCESS,
    payload: bundleAlertDefinition,
  };
}

/**
 * Action which is dispatched when a bundle alert definition fails to be deleted.
 * @param error The error which caused the action.
 */
export function deleteBundleAlertDefinitionFailure(error: SimManagementError): Action<SimManagementError> {
  return {
    error: true,
    type: DELETE_BUNDLE_ALERT_DEFINITION_FAILURE,
    payload: error,
  };
}

/**
 * Action which loads the available commands from the server if required.
 * @param payload The account identifier or telephone number.
 */
export function ensureBundleAlertDefinitions(payload: number | string): Action<number | string> {
  return {
    type: ENSURE_BUNDLE_ALERT_DEFINITIONS,
    payload,
  };
}

/**
 * Action which loads the available command stocks from the server.
 * @param payload A comma-separated list of identifiers for the currently selected stock
 */
export function loadBundleAlertDefinitions(payload: number | string): Action<number | string> {
  return {
    type: LOAD_BUNDLE_ALERT_DEFINITIONS,
    payload,
  };
}

/**
 * Action which sets the load status of the bundle alert definitions to pending.
 * @param payload The information about the request
 */
export function loadBundleAlertDefinitionsPending(payload: number | string): Action<number | string> {
  return {
    type: LOAD_BUNDLE_ALERT_DEFINITIONS_PENDING,
    payload,
  };
}

/**
 * Action which handles when the bundle alert definitions are successfully loaded.
 * @param payload The bundle alert definitions.
 * @param meta The account identifier or telephone number for the request.
 */
export function loadBundleAlertDefinitionsSuccess(
  payload: BundleAlertDefinition[],
  meta: number | string
): ActionMeta<BundleAlertDefinition[], number | string> {
  const type = LOAD_BUNDLE_ALERT_DEFINITIONS_SUCCESS;
  return {
    type,
    payload,
    meta,
  };
}

/**
 * Action which handles when there is a failure to load the bundle alert definitions.
 * @param error The error.
 * @param meta The account identifier or telephone number for the request.
 */
export function loadBundleAlertDefinitionsFailure(
  error: SimManagementError,
  meta: number | string
): ActionMeta<SimManagementError, number | string> {
  return {
    error: true,
    type: LOAD_BUNDLE_ALERT_DEFINITIONS_FAILURE,
    payload: error,
    meta,
  };
}

/**
 * Dispatched to perform an operation on an existing bundle alert definition.
 * @param payload An object containing the updated bundle alert definition data.
 * @param resolve Optional function which will be called on success.
 * @param reject Optional function which will be called on failure.
 */
export function updateBundleAlertDefinition(
  payload: BundleAlertDefinitionInputUpdate,
  resolve?: ResolveFunc<BundleAlertDefinition>,
  reject?: RejectFunc
): PromiseAction<BundleAlertDefinitionInputUpdate, BundleAlertDefinition> {
  const type = UPDATE_BUNDLE_ALERT_DEFINITION;
  return { type, payload, resolve, reject };
}

/**
 * Action which is dispatched when a bundle alert definition fails to update.
 * @param error An object containing the error.
 */
export function updateBundleAlertDefinitionFailure(error: SimManagementError): Action<SimManagementError> {
  return {
    error: true,
    type: UPDATE_BUNDLE_ALERT_DEFINITION_FAILURE,
    payload: error,
  };
}

/**
 * Action which is dispatched when a bundle alert definition has been successfully updated.
 * @param bundleAlertDefinition An object representing the saved bundle alert definition.
 */
export function updateBundleAlertDefinitionSuccess(
  bundleAlertDefinition: BundleAlertDefinition
): Action<BundleAlertDefinition> {
  return {
    type: UPDATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
    payload: bundleAlertDefinition,
  };
}
